<template>
    <app-layout>
        <page-header :title="trans('locations.overview.title')" title-in-app-header>
            <div class="mt-3 flex flex-wrap items-start gap-3">
                <filter-bar v-model="queryParams" :config="filterBarConfig" class="min-w-0 flex-1" />
                <jet-button
                    v-if="hasAnyPermission('location.create')"
                    class="shrink-0"
                    type="button"
                    variant="secondary"
                    @click="$inertia.visit(route('locations.create'))"
                >
                    {{ trans('locations.overview.action.add_airport') }}
                </jet-button>
            </div>
        </page-header>
        <data-table
            :actions="actions"
            :columns="visibleFields"
            :items="transformedLocations"
            :no-items-view="noItemsView"
            :query-params="queryParams"
            @row-clicked="editAirport"
        >
            <template #name="{ item }">
                <td class="align-middle">
                    <div>
                        {{ item.name }}
                    </div>
                    <div class="d-flex align-items-center">
                        <current-time :timezone="item.timezone" />
                    </div>
                </td>
            </template>

            <template #country="{ item }">
                <td class="align-middle">
                    <div class="d-flex align-items-center text-center">
                        <icon v-if="item.countryCodeAlpha2" :name="'cif-' + item.countryCodeAlpha2" class="mx-1" />
                        {{ item.country }}
                    </div>
                </td>
            </template>

            <template #usersCount="{ item }">
                <td class="align-middle">
                    <div class="d-flex align-items-center text-center">
                        <div
                            v-c-tooltip="{
                                content: trans('locations.overview.cell.users.tooltip.total'),
                            }"
                        >
                            {{ item.totalUsersCount }}
                        </div>
                    </div>
                </td>
            </template>

            <template #assetsCount="{ item }">
                <td class="align-middle">
                    <div class="d-flex align-items-center text-center">
                        {{ item.assetsCount }}
                    </div>
                </td>
            </template>
        </data-table>
        <delete-location-modal />
    </app-layout>
</template>
<script>
import AppLayout from '@/Layouts/AppLayout.vue';
import JetButton from '@/Jetstream/Button.vue';
import Pagination from '@/Components/Pagination.vue';
import CustomCardHeader from '@/Components/CustomCardHeader.vue';
import CurrentTime from '@/Components/CurrentTime.vue';
import Card from '@/Components/Card.vue';
import DataTable from '@/Components/DataTable.vue';
import PageHeader from '@/Components/PageHeader.vue';
import FilterBar from '@/Components/FilterBar.vue';
import { eventBus, events } from '@/eventBus.js';
import { useExportsStore } from '@/stores/exports.js';
import DeleteLocationModal from '@/Pages/Locations/DeleteLocationModal.vue';
import { computed, ref } from 'vue';
import { isDesktop } from '@/Utils/UseMedia';
import { route } from 'ziggy-js';
import { $http } from '@/bootstrap.js';
import { trans } from '@/mixins/i18n';

const isNumber = (val) => typeof val === 'number';

export default {
    components: {
        DeleteLocationModal,
        FilterBar,
        PageHeader,
        DataTable,
        Card,
        CurrentTime,
        AppLayout,
        CustomCardHeader,
        JetButton,
        Pagination,
    },
    setup() {
        const queryParams = ref(null);

        const filterBarConfig = computed(() => ({
            route: route('locations.index'),
            routeOptions: { only: ['locations'] },
            defaultSort: { column: 'icaoCode', asc: true },
            filters: [{ type: 'search', key: 'search', placeholder: trans('locations.overview.filter.placeholder') }],
        }));

        return {
            queryParams,
            filterBarConfig,
            isDesktop,
        };
    },

    data() {
        return {
            allFields: [
                {
                    key: 'icaoCode',
                    label: this.trans('locations.overview.cell.icao'),
                },
                {
                    key: 'iataCode',
                    label: this.trans('locations.overview.cell.iata'),
                },
                {
                    key: 'name',
                    label: this.trans('locations.overview.cell.name'),
                },
                {
                    key: 'country',
                    label: this.trans('locations.overview.cell.country'),
                },
                {
                    key: 'specificLocationsCount',
                    label: this.trans('locations.overview.cell.specific_locations'),
                },
                {
                    key: 'usersCount',
                    label: this.trans('locations.overview.cell.users'),
                },
                {
                    key: 'assetsCount',
                    label: this.trans('locations.overview.cell.assets'),
                },
            ],
            noItemsView: {
                noItems: this.trans('locations.overview.results.empty'),
            },
            actions: [
                {
                    condition: (airport) => airport.isTrainingLocation && this.isSuperAdmin,
                    tooltip: this.trans('locations.overview.tooltip.training_material'),
                    type: 'primary',
                    click: this.generateTrainingMaterial,
                    icon: 'cil-qr-code',
                },
                {
                    permissions: ['location.update'],
                    tooltip: this.trans('locations.overview.tooltip.edit_airport'),
                    type: 'primary',
                    click: this.editAirport,
                    icon: 'cil-pencil',
                },
                {
                    permissions: ['location.delete'],
                    tooltip: this.trans('locations.overview.tooltip.delete_airport'),
                    type: 'danger',
                    click: this.confirmDeleteAirport,
                    icon: 'cil-trash',
                },
            ],
        };
    },

    computed: {
        isSuperAdmin() {
            return (this.$page.props.auth?.user?.roles ?? []).includes('super-admin');
        },
        locations() {
            return this.$page.props.locations;
        },
        visibleFields() {
            return this.allFields;
        },
        transformedLocations() {
            const notAvailable = this.trans('general.not_available');

            if (!this.locations) {
                return null;
            }

            const { meta, links, data } = this.locations;
            return {
                meta,
                links,
                data: data.map((loc) => ({
                    id: loc.id,
                    icaoCode: loc.icaoCode || notAvailable,
                    iataCode: loc.iataCode || notAvailable,
                    name: loc.name || notAvailable,
                    country: loc.countryCodeAlpha2 || notAvailable,
                    countryCodeAlpha2: loc.countryCodeAlpha2?.toLowerCase(),
                    totalActiveUsersCount: isNumber(loc.totalActiveUsersCount) ? loc.totalActiveUsersCount : notAvailable,
                    totalUsersCount: isNumber(loc.totalUsersCount) ? loc.totalUsersCount : notAvailable,
                    assetsCount: isNumber(loc.assetsCount) ? loc.assetsCount : notAvailable,
                    specificLocationsCount: isNumber(loc.specificLocationsCount) ? loc.specificLocationsCount : notAvailable,
                    timezone: loc.timezone?.identifier,
                    isTrainingLocation: loc.isTrainingLocation === true,
                })),
            };
        },
    },

    methods: {
        editAirport(airport) {
            this.$inertia.visit(this.route('locations.edit', airport.id));
        },
        generateTrainingMaterial(airport) {
            $http
                .post(this.route('locations.training-material.generate', airport.id))
                .then((resp) => {
                    // Register with the exports store so the user is polled and shown an
                    // in-app download link as soon as the PDF is ready (no email needed).
                    useExportsStore().addExport(resp.data, 'training_material');
                    eventBus.$emit(events.presentFlashMessage, {
                        type: 'success',
                        payload: {
                            message: this.trans('locations.training_material.flash.started'),
                            status: 'success',
                        },
                    });
                })
                .catch((error) => {
                    eventBus.$emit(events.presentFlashMessage, {
                        payload: error.response?.data,
                    });
                });
        },
        confirmDeleteAirport(airport) {
            if (airport.assetsCount > 0) {
                $http.delete(this.route('locations.destroy', airport.id)).catch((error) => {
                    eventBus.$emit(events.presentFlashMessage, {
                        payload: error.response.data,
                    });
                });

                return;
            }

            eventBus.$emit(events.openDeleteLocationModal, {
                locationId: airport.id,
                name: airport.name,
                type: 'airport',
            });
        },
        getActiveUsers(users) {
            return users?.filter((user) => user.status.value === 'active');
        },
        updateValue(key, value) {
            this.activeQueryParams.form[key] = value;
            this.activeQueryParams.change();
        },
    },
};
</script>
